<template>
  <f7-page name="save-to-buy-create">
    <f7-navbar :title="$t('saveToBuy.createCampaign')" back-link />
    <AppLayout>
      <template #main>
        <!-- Offer not found state -->
        <f7-block v-if="!offer" class="stb-empty-state">
          <f7-icon f7="shopping_bag" size="48" color="var(--f7-on-surface-variant, #8e8e93)" />
          <p class="margin-top-half">{{ $t("saveToBuy.offerNotFound") }}</p>
        </f7-block>

        <template v-else>
          <!-- Product Summary Card -->
          <f7-card class="stb-product-card" inset outline>
            <f7-card-content class="display-flex flex-direction-column gap-2">
              <div class="display-flex align-items-center justify-content-space-between">
                <h2 class="stb-product-name margin-0">{{ offer.title }}</h2>
                <span class="stb-product-badge bg-primary text-on-primary rounded-pill px-2 py-0.5 font-label-sm">{{ $t("saveToBuy.catalogProduct") }}</span>
              </div>
              <p class="stb-product-price margin-0">{{ $t("saveToBuy.individualPrice") }} <strong>{{ fmtCfa(basePrice) }}</strong></p>
              <p class="stb-product-hint margin-0">{{ $t("saveToBuy.createHint") }}</p>
            </f7-card-content>
          </f7-card>

          <!-- Form Sections -->
          <f7-list strong inset media-list outline dividers>
            <f7-list-group>
              <f7-block-title class="margin-top">{{ $t("saveToBuy.campaignDetails") }}</f7-block-title>

              <f7-list-input
                v-model:value="form.title"
                :label="$t('saveToBuy.campaignTitle')"
                :placeholder="$t('saveToBuy.campaignTitlePh')"
                clear-button
                required
                validate
              />
              <f7-list-input
                v-model:value="form.description"
                type="textarea"
                resizable
                :label="$t('saveToBuy.message')"
                :placeholder="$t('saveToBuy.messagePh')"
              />
              <f7-list-input
                v-model:value="form.targetQuantity"
                type="number"
                :label="$t('saveToBuy.unitsNeeded')"
                :placeholder="$t('saveToBuy.unitsNeededPh')"
                min="1"
                step="1"
                required
                validate
              />
              <f7-list-input
                v-model:value="form.endsAt"
                type="datetime-local"
                :label="$t('saveToBuy.ends')"
                required
                validate
              />
              <f7-list-item :title="$t('saveToBuy.tempChat')">
                <template #after>
                  <f7-toggle
                    v-model:checked="form.createTemporaryChat"
                    color="green"
                  />
                </template>
              </f7-list-item>
            </f7-list-group>
          </f7-list>

          <!-- Failure Policy - Radio Group -->
          <f7-block strong inset class="margin-top">
            <f7-block-title>{{ $t("saveToBuy.failurePolicy") }}</f7-block-title>
            <p class="stb-policy-hint">{{ $t("saveToBuy.failurePolicyHint") }}</p>
            <f7-list strong inset>
              <f7-list-item
                v-for="p in FAILURE_POLICIES"
                :key="p.code"
                :title="$t(p.labelKey)"
                radio
                name="failurePolicy"
                :checked="form.failurePolicy === p.code"
                @change="form.failurePolicy = p.code"
              >
                <template #after>
                  <f7-icon v-if="form.failurePolicy === p.code" f7="checkmark" size="20" color="var(--f7-theme-color)" />
                </template>
              </f7-list-item>
            </f7-list>
          </f7-block>

          <!-- Seller Tiers Preview -->
          <f7-block strong inset class="margin-top">
            <f7-block-title>{{ $t("saveToBuy.sellerTiers") }}</f7-block-title>
            <f7-list strong inset>
              <f7-list-item
                v-for="tier in tiers"
                :key="tier.minimumQuantity"
                :title="$t('saveToBuy.buyers', { count: tier.minimumQuantity })"
                :after="fmtCfa(tier.unitPrice)"
              >
                <template #media>
                  <span class="material-symbols-outlined text-primary" style="font-variation-settings: 'FILL' 1;">trending_down</span>
                </template>
                <template #subtitle>
                  <span class="stb-tier-saving" v-if="tierSavings(tier) > 0">
                    {{ $t('saveToBuy.savings', { amount: fmtCfa(tierSavings(tier)) }) }}
                  </span>
                </template>
              </f7-list-item>
            </f7-list>
          </f7-block>

          <!-- Submit Action -->
          <f7-block strong inset class="margin-top padding-top-half">
            <f7-button
              fill
              large round raised
              :disabled="!canSubmit"
              @click="submit"
              class="stb-submit-btn"
            >
              <span v-if="!submitting">{{ $t("saveToBuy.createAndInvite") }}</span>
              <span v-else class="display-flex align-items-center gap-2">
                <f7-preloader size="16" />
                {{ $t("common.creating") }}
              </span>
            </f7-button>
            <p class="stb-submit-hint margin-top-half" v-if="!canSubmit">
              {{ $t("saveToBuy.fillRequiredFields") }}
            </p>
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { fmtCfa } from "../constants/listing-options.js";
import { useMarketplaceStore } from "../js/pinia-stores/marketplace.js";
import {
  useSaveToBuyStore,
  FAILURE_POLICIES,
  suggestTiers,
  offerPrice,
} from "../js/pinia-stores/saveToBuy.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import i18n from "../js/i18n.js";

const props = defineProps({
  groupId: { type: String, required: true },
  offerId: { type: String, required: true },
});

const { t } = i18n.global;
const saveToBuyStore = useSaveToBuyStore();
const marketplaceStore = useMarketplaceStore();
const groupsStore = useGroupsStore();

const offer = ref(null);
const submitting = ref(false);
const basePrice = computed(() =>
  offer.value ? offerPrice(offer.value) : null,
);
const tiers = computed(() =>
  basePrice.value ? suggestTiers(basePrice.value) : [],
);

const form = reactive({
  title: "",
  description: "",
  targetQuantity: 10,
  endsAt: toLocalInput(Date.now() + 7 * 86400000),
  createTemporaryChat: true,
  failurePolicy: "REFUND_ALL",
});

const canSubmit = computed(
  () =>
    !!offer.value &&
    form.title.trim().length > 0 &&
    Number(form.targetQuantity) > 0 &&
    !!form.endsAt,
);

function toLocalInput(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId).catch(() => {}),
    marketplaceStore.refreshAll(props.groupId).catch(() => {}),
  ]);
  const offers = marketplaceStore.published(props.groupId);
  offer.value = offers.find((o) => o.id === props.offerId) || null;
  if (offer.value) {
    form.title = offer.value.title;
    form.description = offer.value.description || "";
  }
});

function tierSavings(tier) {
  if (!basePrice.value) return 0;
  return Number(basePrice.value) - Number(tier.unitPrice);
}

async function submit() {
  if (!canSubmit.value) return;
  submitting.value = true;
  f7.preloader.show();
  try {
    const result = await saveToBuyStore.createCampaign({
      productId: offer.value.id,
      variantId: null,
      sourceGroupId: props.groupId,
      title: form.title.trim(),
      description: form.description.trim(),
      individualPrice: basePrice.value,
      targetAmount: basePrice.value * Number(form.targetQuantity),
      targetQuantity: Number(form.targetQuantity),
      currency: offer.value.currency || "XOF",
      startsAt: new Date().toISOString(),
      endsAt: new Date(form.endsAt).toISOString(),
      failurePolicy: form.failurePolicy,
      allOrNothing: true,
      createTemporaryChat: form.createTemporaryChat,
      sourceType: "CATALOGUE_PRODUCT",
      priceTiers: tiers.value,
    });
    f7.preloader.hide();
    f7.toast.show({ text: t("saveToBuy.campaignCreated"), closeTimeout: 2000 });
    f7.views.main.router.navigate(
      `/groups/${props.groupId}/save-to-buy/campaigns/${result.id}`,
    );
  } catch (error) {
    f7.preloader.hide();
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.stb-empty-state {
  text-align: center;
  padding: 64px 24px;
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 16px;
  margin: 16px;
}

.stb-product-card {
  margin: 16px;
  background: var(--f7-card-bg-color, #fff);
}

.stb-product-name {
  font-size: 17px;
  font-weight: 700;
  color: var(--f7-text-color, #161d18);
}

.stb-product-badge {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.stb-product-price {
  font-size: 15px;
  color: var(--f7-theme-color);
}

.stb-product-hint {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
}

.stb-policy-hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-bottom: 8px;
}

.stb-tier-saving {
  font-size: 11px;
  color: var(--f7-theme-color);
  font-weight: 600;
}

.stb-submit-btn {
  width: 100%;
}

.stb-submit-hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-align: center;
}
</style>